import type { ReactNode } from 'react';
import { Link, usePage } from '@inertiajs/react';
import type { SharedProps } from '../types';

// 后台骨架。不复用 AppLayout，也不复用 WikiLayout：
//
// AppLayout 带 Header / TimerBar / Footer 和 container mx-auto p-4 —— 后台是
// 高密度表格，那套居中窄容器会把表挤成一小块，而 TimerBar 与 Footer 在后台
// 页面上没有落点。WikiLayout 是给读长文的阅读列宽，同理不适用。
//
// 顶部那条 topbar 是全站唯一的深色元素，作用只有一个：一眼知道自己在后台。
// 用户否掉了整体深色后台风，但那个需求还在（spec §8.3）。

type NavLeaf = { href: string; label: string; count?: string; external?: boolean };
type NavGroup = { label?: string; items: NavLeaf[] };

// 六个页面的导航。本期逐个 Task 接上，未实现的地址先不列 ——
// 列一个 404 的链接比不列更糟。
const NAV: NavGroup[] = [
  {
    items: [
      { href: '/admin', label: '总览' },
      { href: '/admin/users', label: '用户' },
    ],
  },
  {
    label: '内容巡检',
    items: [
      { href: '/admin/content/tasks', label: '任务' },
      { href: '/admin/content/wiki', label: 'Wiki' },
      { href: '/admin/content/worklogs', label: 'Worklog' },
      { href: '/admin/content/projects', label: '项目' },
    ],
  },
  {
    label: '凭证与会话',
    items: [
      { href: '/admin/credentials/api-tokens', label: 'API Token' },
      { href: '/admin/credentials/jwt', label: 'JWT' },
      { href: '/admin/credentials/oauth-apps', label: 'OAuth 应用' },
      { href: '/admin/credentials/oauth-tokens', label: 'OAuth 授权' },
      // 用户自己配的 AI key 就是第五类凭证 —— 与上面四类并列，而不是新开一页。
      { href: '/admin/credentials/ai-keys', label: 'AI Key' },
    ],
  },
  {
    label: '系统',
    // /jobs 是 MissionControl 的 engine，不是 Inertia 页面 —— 必须走普通 <a>。
    // 用 Inertia 的 <Link> 会发一个 XHR，拿回整页 HTML 而不是 Inertia 响应，
    // 结果是点了没反应、控制台一条报错。
    items: [
      // AI 用量挂「系统」而不是「凭证与会话」：那是事实，不是凭证。
      { href: '/admin/ai-usage', label: 'AI 用量' },
      { href: '/jobs', label: '任务队列', external: true },
    ],
  },
];

export default function AdminLayout({ children }: { children: ReactNode }) {
  const { props, url } = usePage<SharedProps>();
  const email = props.auth?.user?.email ?? '';

  // 前缀匹配，但 /admin 本身要精确匹配 —— 否则它在任何子页面上都亮着。
  const isActive = (href: string) =>
    href === '/admin' ? url === '/admin' : url.startsWith(href);

  return (
    <div className="flex h-screen flex-col bg-admin-page text-admin-ink">
      <header className="flex h-[38px] flex-none items-center gap-2.5 bg-admin-topbar px-3">
        <span className="text-[13px] font-semibold tracking-tight text-white">
          asaptodo <span className="ml-1 text-[12px] font-normal text-admin-muted">admin</span>
        </span>
        <span className="flex-1" />
        <span className="rounded border border-white/15 bg-white/10 px-1.5 py-px text-[11px] text-admin-topbarInk">
          只读
        </span>
        <span className="text-[12px] text-admin-topbarInk">{email}</span>
      </header>

      <div className="grid min-h-0 flex-1 grid-cols-[214px_minmax(0,1fr)]">
        <nav className="overflow-y-auto border-r border-admin-line bg-admin-nav py-2.5">
          {NAV.map((group, gi) => (
            <div key={gi} className="mb-1">
              {group.label && (
                <div className="px-3.5 pb-1.5 pt-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-admin-muted">
                  {group.label}
                </div>
              )}
              {group.items.map((item) => {
                const cls = `flex w-full items-center gap-2 border-l-2 px-3.5 py-1.5 text-[13px] ${
                  isActive(item.href)
                    ? 'border-admin-accent bg-admin-surface font-semibold text-admin-ink'
                    : 'border-transparent text-admin-ink2 hover:bg-black/[.035] hover:text-admin-ink'
                }`;
                const body = (
                  <>
                    {item.label}
                    {item.external && (
                      <span className="text-[10px] text-admin-muted">↗</span>
                    )}
                    {item.count && (
                      <span className="ml-auto font-mono text-[11px] font-normal text-admin-muted">
                        {item.count}
                      </span>
                    )}
                  </>
                );

                return item.external ? (
                  <a key={item.href} href={item.href} className={cls}>
                    {body}
                  </a>
                ) : (
                  <Link key={item.href} href={item.href} className={cls}>
                    {body}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <main className="min-w-0 overflow-y-auto px-[18px] pb-10 pt-4">{children}</main>
      </div>
    </div>
  );
}
